import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Icon } from '@/components/ui/icon'
import { DARK_THEMES, PREFERENCES, resolveTheme, useTheme } from '@/hooks/useTheme'

import { ThemePreview } from './ThemePreview'

/**
 * Appearance settings (design board 16): System — which follows the device —
 * then the eight themes, as a radio group of preview cards, opened from the
 * header.
 *
 * Native radio inputs rather than buttons with `role="radio"`: the arrow keys,
 * the single tab stop and the checked state all come with them. Choosing a
 * card applies the theme at once — there is nothing to save — and the
 * provider persists it.
 *
 * The trigger names the theme in use, not what clicking does: a screen reader
 * user needs to know what the theme *is*.
 */
export function ThemePicker() {
  const { t } = useTranslation()
  const { preference, setTheme } = useTheme()
  const current = t('theme.current', { name: t(`theme.names.${preference}`) })
  // System previews whichever theme the device asks for right now.
  const deviceIsDark =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={current} title={current}>
          <Icon name="palette" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-200">
        <DialogHeader>
          <DialogTitle>{t('theme.title')}</DialogTitle>
          <DialogDescription>{t('theme.description')}</DialogDescription>
        </DialogHeader>
        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          <legend className="sr-only">{t('theme.label')}</legend>
          {PREFERENCES.map((id) => (
            <label
              key={id}
              className="bg-card has-checked:border-focus-ring has-checked:ring-focus-ring has-focus-visible:outline-focus-ring hover:border-border-strong flex cursor-pointer flex-col gap-3 rounded-lg border p-3 shadow-xs transition-[border-color,box-shadow] hover:shadow-sm has-checked:ring-1 has-focus-visible:outline-2 has-focus-visible:outline-offset-2"
            >
              <ThemePreview theme={id === 'system' ? resolveTheme(id, deviceIsDark) : id} />
              <span className="flex items-start gap-2.5">
                <input
                  type="radio"
                  name="theme"
                  value={id}
                  checked={preference === id}
                  onChange={() => setTheme(id)}
                  className="accent-focus-ring mt-0.5 size-4 shrink-0 cursor-pointer focus-visible:outline-none"
                />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  {/* The mode rides on the name's line, so the description
                      has the card's whole width rather than a sliver. */}
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm leading-5 font-semibold">
                      {t(`theme.names.${id}`)}
                    </span>
                    <span className="bg-neutral-subtle text-neutral-foreground text-2xs inline-flex h-5 shrink-0 items-center rounded-sm px-1.5 font-semibold">
                      {t(
                        id === 'system'
                          ? 'theme.mode.auto'
                          : DARK_THEMES.has(id)
                            ? 'theme.mode.dark'
                            : 'theme.mode.light',
                      )}
                    </span>
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {t(`theme.descriptions.${id}`)}
                  </span>
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      </DialogContent>
    </Dialog>
  )
}
